<template>
  <div class="thank-you-container">
    <div class="thank-you-card">
      <!-- Success Animation -->
      <div class="success-icon-wrapper">
        <div class="success-circle">
          <b-icon :icon="isCardSaved ? 'credit-card-2-front' : 'check-lg'" class="check-icon"></b-icon>
        </div>
      </div>

      <!-- Card Saved Message -->
      <template v-if="isCardSaved">
        <h1 class="thank-you-title">{{ $t('checkout.cardSavedTitle') }}</h1>
        <p class="thank-you-message">{{ $t('checkout.cardSavedMessage') }}</p>

        <div class="confirmation-box">
          <b-icon icon="shield-check" class="confirmation-icon"></b-icon>
          <span>{{ $t('checkout.cardSavedConfirmation') }}</span>
        </div>

        <div class="action-buttons">
          <b-button
            variant="primary"
            to="/admin/projects/settings?subtab=payment-methods#account"
            size="lg"
            class="primary-action"
          >
            <b-icon icon="credit-card" class="mr-2"></b-icon>
            {{ $t('checkout.viewPaymentMethods') }}
          </b-button>

          <b-button
            variant="outline-secondary"
            to="/admin/dashboard"
            size="lg"
            class="secondary-action"
          >
            <b-icon icon="grid-fill" class="mr-2"></b-icon>
            {{ $t('backToPlatform') }}
          </b-button>
        </div>
      </template>

      <!-- Order Thank You Message -->
      <template v-else>
        <h1 class="thank-you-title">{{ $t('checkout.thankYou') }}</h1>
        <p class="thank-you-message">{{ $t('checkout.thankYouMessage') }}</p>

        <div class="confirmation-box">
          <b-icon icon="envelope-check" class="confirmation-icon"></b-icon>
          <span>{{ $t('checkout.confirmationEmailSent') }}</span>
        </div>

        <div class="action-buttons">
          <b-button
            variant="primary"
            to="/admin/dashboard"
            size="lg"
            class="primary-action"
          >
            <b-icon icon="grid-fill" class="mr-2"></b-icon>
            {{ $t('backToPlatform') }}
          </b-button>

          <b-button
            variant="outline-secondary"
            to="/admin/projects/settings#account"
            size="lg"
            class="secondary-action"
          >
            <b-icon icon="puzzle" class="mr-2"></b-icon>
            {{ $t('checkout.viewPurchases') }}
          </b-button>
        </div>
      </template>

      <!-- Support Link -->
      <p class="support-text">
        {{ $t('checkout.needHelp') }}
        <a href="mailto:support@protokol.rs" class="support-link">{{ $t('checkout.contactSupport') }}</a>
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.thank-you-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.thank-you-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 24px;
  padding: 60px 48px;
  max-width: 560px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: slideUp 0.5s ease-out;

  @media (max-width: 576px) {
    padding: 40px 24px;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.success-icon-wrapper {
  margin-bottom: 32px;
}

.success-circle {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.4s ease-out 0.2s both;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);

  .check-icon {
    color: var(--text-primary);
    font-size: 3rem;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.thank-you-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 16px;
  animation: fadeIn 0.4s ease-out 0.3s both;

  @media (max-width: 576px) {
    font-size: 2rem;
  }
}

.thank-you-message {
  font-size: 1.15rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 32px;
  animation: fadeIn 0.4s ease-out 0.4s both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.confirmation-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  margin-bottom: 40px;
  color: #6ee7b7;
  font-size: 0.95rem;
  animation: fadeIn 0.4s ease-out 0.5s both;

  .confirmation-icon {
    font-size: 1.25rem;
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
  animation: fadeIn 0.4s ease-out 0.6s both;
}

.primary-action {
  font-size: 1.1rem;
  font-weight: 600;
  padding: 14px 24px;
  border-radius: 12px;
  transition: all 0.2s ease;
  color: var(--text-primary) !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
    color: var(--text-primary) !important;
  }
}

.secondary-action {
  font-size: 1rem;
  font-weight: 500;
  padding: 12px 24px;
  border-radius: 12px;
  border-color: $dark-border2;
  color: var(--text-primary);
  transition: all 0.2s ease;

  &:hover {
    background: $dark-hard2-background;
    border-color: var(--text-muted);
    color: var(--text-primary);
  }
}

.support-text {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin: 0;
  animation: fadeIn 0.4s ease-out 0.7s both;
}

.support-link {
  color: $protokol-royal-active;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #8b9aef;
    text-decoration: underline;
  }
}
</style>

<script>
export default {
  name: 'ThankYou',
  computed: {
    isCardSaved() {
      return this.$route.query.cardSaved === '1'
    }
  },
  mounted() {
    const checkoutPage = new BroadcastChannel('checkout')
    checkoutPage.postMessage({ type: 'checkout_finished' })

    if (!this.isCardSaved) {
      this.$gtag.event('purchase_complete')
    }
  }
}
</script>